<template>
  <div class="homepage">
    <!-- Header/Navbar -->
    <header class="navbar-header">
      <div class="container">
        <div class="navbar-content">
          <div class="logo-text">Daily Timeline</div>
          <nav class="nav-links">
            <router-link :to="{ name: 'Login' }" class="nav-link">Sign In</router-link>
            <router-link :to="{ name: 'Registration' }" class="nav-link nav-link-primary">Get Started</router-link>
          </nav>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <div class="container hero-content">
        <div class="row align-items-center">
          <div class="col-lg-6 col-md-12 hero-text">
            <div class="hero-badge">Your Personal Memory Platform</div>
            <h1 class="hero-title">
              What's the most <span class="highlight">precious gift?</span>
            </h1>
            <h2 class="hero-subtitle">Memories.</h2>
            <p class="hero-description">
              Share and showcase your photos and important life events with the people you care about, without sharing them with the whole world. Create your own private timeline, like your own, personal social media!
            </p>
            <div class="hero-buttons">
              <router-link
                :to="{ name: 'Registration' }"
                class="btn btn-primary btn-lg cta-button"
              >
                <span>Get Started Free</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </router-link>
              <router-link
                :to="{ name: 'Login' }"
                class="btn btn-outline btn-lg secondary-button"
              >
                Sign In
              </router-link>
            </div>
          </div>
          <div class="col-lg-6 col-md-12 hero-image">
            <div class="image-container">
              <div class="floating-card card-1">
                <div class="card-icon">📸</div>
                <div class="card-text">Share Photos</div>
              </div>
              <div class="floating-card card-2">
                <div class="card-icon">🎉</div>
                <div class="card-text">Celebrate Moments</div>
              </div>
              <div class="floating-card card-3">
                <div class="card-icon">🔒</div>
                <div class="card-text">Stay Private</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <div class="section-header text-center">
          <h2 class="section-title">Why Choose Our Platform?</h2>
          <p class="section-subtitle">Everything you need to preserve and share your precious memories</p>
        </div>
        <div class="row features-grid">
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2.45825 12C3.73253 7.94288 7.52281 5 12.0004 5C16.4781 5 20.2684 7.94291 21.5426 12C20.2684 16.0571 16.4781 19 12.0004 19C7.52281 19 3.73251 16.0571 2.45825 12Z" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Privacy First</h3>
              <p class="feature-description">
                Share your moments only with those who matter. Complete control over who sees your timeline.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Organized Timeline</h3>
              <p class="feature-description">
                View your life events chronologically. Never lose track of your precious memories.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Collaborative</h3>
              <p class="feature-description">
                Invite loved ones to contribute to shared timelines. Build memories together.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#702670"/>
                  <path d="M21 15L16 10L5 21" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Photo & Video Storage</h3>
              <p class="feature-description">
                Upload unlimited photos and videos. Keep all your memories in one secure place.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3.27002 6.96L12 12.01L20.73 6.96" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 22.08V12" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Cross-Platform</h3>
              <p class="feature-description">
                Access your timeline from any device. Your memories are always within reach.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="feature-card">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 6V12L16 14" stroke="#702670" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="feature-title">Real-Time Updates</h3>
              <p class="feature-description">
                See new posts instantly. Stay connected with your loved ones' latest moments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works-section">
      <div class="container">
        <div class="section-header text-center">
          <h2 class="section-title">How It Works</h2>
          <p class="section-subtitle">Get started in three simple steps</p>
        </div>
        <div class="row steps-container">
          <div class="col-lg-4 col-md-12 mb-4">
            <div class="step-card">
              <div class="step-number">1</div>
              <h3 class="step-title">Create Your Timeline</h3>
              <p class="step-description">
                Sign up and create your personal timeline in seconds. No credit card required.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-12 mb-4">
            <div class="step-card">
              <div class="step-number">2</div>
              <h3 class="step-title">Add Your Memories</h3>
              <p class="step-description">
                Upload photos, videos, and stories. Organize them chronologically with ease.
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-md-12 mb-4">
            <div class="step-card">
              <div class="step-number">3</div>
              <h3 class="step-title">Share & Connect</h3>
              <p class="step-description">
                Invite loved ones to view or contribute. Build your shared memory collection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content text-center">
          <h2 class="cta-title">Ready to Start Building Your Timeline?</h2>
          <p class="cta-description">
            Join thousands of people who are already preserving their precious memories
          </p>
          <div class="cta-buttons">
            <router-link
              :to="{ name: 'Registration' }"
              class="btn btn-light btn-lg cta-button-white"
            >
              Create Your Timeline
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-main text-center">
          <h3 class="footer-brand">Daily Timeline</h3>
          <p class="footer-description">
            Your personal memory platform. Private, secure, and built for what matters most.
          </p>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2026 Daily Timeline. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Home2',
  data() {
    return {
      // Add any data properties here if needed in the future
    };
  },
  mounted() {
    // Add smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
  },
  beforeUnmount() {
    document.documentElement.style.scrollBehavior = 'auto';
  },
};
</script>

<style scoped>
/* Global Styles */
.homepage {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow-x: hidden;
}

/* Header/Navbar */
.navbar-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(112, 38, 112, 0.95);
  backdrop-filter: blur(10px);
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.navbar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-text {
  font-family: 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive;
  font-size: 2.5rem;
  font-weight: normal;
  color: white;
  font-style: italic;
  letter-spacing: 2px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.nav-links {
  display: flex;
  gap: 24px;
  align-items: center;
}

.nav-link {
  color: white;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: opacity 0.3s ease;
}

.nav-link:hover {
  opacity: 0.8;
  color: white;
  text-decoration: none;
}

.nav-link-primary {
  background: white;
  color: #702670;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
}

.nav-link-primary:hover {
  opacity: 0.9;
  color: #702670;
  transform: translateY(-1px);
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 50%, #5a1f5a 100%);
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 140px 0 80px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("../assets/homepage6.png") center center;
  background-size: cover;
  opacity: 0.1;
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-text {
  color: white;
  padding: 40px 20px;
  animation: fadeInUp 0.8s ease-out;
  text-align: center;
}

.hero-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 16px;
}

.hero-title .highlight {
  background: linear-gradient(90deg, #fff 0%, #f8d7ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 24px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-description {
  font-size: 1.25rem;
  line-height: 1.7;
  margin-bottom: 40px;
  opacity: 0.95;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.hero-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 60px;
  justify-content: center;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #702670;
  padding: 16px 32px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  color: #702670;
  text-decoration: none;
}

.secondary-button {
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 16px 32px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
}

.secondary-button:hover {
  background: white;
  color: #702670;
  text-decoration: none;
  transform: translateY(-2px);
}

.hero-image {
  position: relative;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.floating-card {
  position: absolute;
  background: white;
  padding: 20px 30px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 12px;
  animation: float 3s ease-in-out infinite;
}

.floating-card.card-1 {
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.floating-card.card-2 {
  top: 50%;
  right: 10%;
  animation-delay: 1s;
}

.floating-card.card-3 {
  bottom: 20%;
  left: 20%;
  animation-delay: 2s;
}

.card-icon {
  font-size: 2rem;
}

.card-text {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

/* Features Section */
.features-section {
  padding: 100px 0;
  background: #f8f9fa;
}

.section-header {
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

.features-grid {
  margin-top: 40px;
}

.feature-card {
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(112, 38, 112, 0.2);
}

.feature-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f8e6f8 0%, #e8d0e8 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.feature-description {
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
}

/* How It Works Section */
.how-it-works-section {
  padding: 100px 0;
  background: white;
}

.steps-container {
  margin-top: 60px;
}

.step-card {
  text-align: center;
  padding: 40px 20px;
}

.step-number {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 100%);
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 8px 24px rgba(112, 38, 112, 0.3);
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.step-description {
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
  max-width: 400px;
  margin: 0 auto;
}

/* CTA Section */
.cta-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 100%);
  color: white;
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 20px;
}

.cta-description {
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.95;
}

.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-button-white {
  background: white;
  color: #702670;
  padding: 16px 40px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.cta-button-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  color: #702670;
  text-decoration: none;
}

/* Footer */
.footer {
  background: #1a1a1a;
  color: white;
  padding: 60px 0 30px;
}

.footer-main {
  margin-bottom: 20px;
}

.footer-brand {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 20px;
}

.footer-description {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.1rem;
}

.footer-bottom {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Responsive Design */
@media (max-width: 992px) {
  .logo-text {
    font-size: 2rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1.1rem;
  }

  .hero-image {
    height: 400px;
    margin-top: 40px;
  }

  .section-title {
    font-size: 2rem;
  }

  .cta-title {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .navbar-header {
    padding: 15px 0;
  }

  .logo-text {
    font-size: 1.8rem;
  }

  .nav-links {
    gap: 12px;
  }

  .nav-link {
    font-size: 0.9rem;
  }

  .nav-link-primary {
    padding: 8px 16px;
  }

  .hero-section {
    padding: 100px 0 60px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .hero-description {
    font-size: 1rem;
  }

  .hero-buttons {
    flex-direction: column;
  }

  .cta-button,
  .secondary-button {
    width: 100%;
    justify-content: center;
  }

  .hero-image {
    height: 300px;
  }

  .floating-card {
    padding: 15px 20px;
  }

  .card-text {
    font-size: 0.9rem;
  }

  .features-section,
  .how-it-works-section,
  .cta-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .cta-title {
    font-size: 1.8rem;
  }

  .cta-description {
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .logo-text {
    font-size: 1.5rem;
  }

  .nav-link {
    font-size: 0.85rem;
  }

  .nav-link-primary {
    padding: 6px 12px;
    font-size: 0.85rem;
  }

  .hero-title {
    font-size: 1.75rem;
  }

  .hero-subtitle {
    font-size: 1.25rem;
  }

  .floating-card {
    padding: 12px 16px;
  }

  .card-icon {
    font-size: 1.5rem;
  }

  .card-text {
    font-size: 0.8rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Home2.vue